// App — route tree for yahoogle-compliance-auditor.
//
// Route hierarchy:
//   /login            → SignInPage (public)
//   RequireAuth (layout) — all routes below require a session
//     /connect        → ConnectESPPage (auth required — Mailchimp POST needs session)
//     IntegrationGate (layout — redirects to /connect if no active ESP)
//       AppShell (layout)
//         /           → DashboardPage
//         /domains    → DomainsPage
//         /domains/:domainId/scans → ScanDetailPage

import { useEffect } from "react";
import { Routes, Route, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Globe } from "lucide-react";
import { AppShell } from "@avagency/frontend/components/shell/AppShell";
import { RequireAuth } from "@avagency/frontend/components/auth/RequireAuth";
import { SignInPage } from "./pages/SignInPage";
import { ConnectESPPage } from "./pages/ConnectESPPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DomainsPage } from "./pages/DomainsPage";
import { ScanDetailPage } from "./pages/ScanDetailPage";
import { IntegrationGate } from "./components/IntegrationGate";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";

export default function App() {
  useEffect(() => {
    document.title = "Yahoogle — Email Compliance Auditor";
  }, []);

  return (
    <Routes>
      {/* ── Public ── */}
      <Route path="/login" element={<SignInPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* ── Protected — session required ── */}
      <Route element={<RequireAuth redirectTo="/login" />}>
        {/* /connect: auth required (Mailchimp key-paste POSTs to session-guarded endpoint) */}
        <Route path="/connect" element={<ConnectESPPage />} />

        {/* Redirects to /connect if no active ESP integration */}
        <Route element={<IntegrationGate />}>
          <Route element={<AppShellLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="/domains" element={<DomainsPage />} />
            <Route
              path="/domains/:domainId/scans"
              element={<ScanDetailPage />}
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

// AppShell accepts `children` not <Outlet/> automatically, so wrap it.
function AppShellLayout() {
  return (
    <AppShell
      productSlug="yahoogle"
      topbar={<TopbarBrand />}
      sidebar={<Sidebar />}
      sidebarLabel="Yahoogle navigation"
    >
      <Outlet />
    </AppShell>
  );
}

function TopbarBrand() {
  return (
    <div className="yg-topbar-brand">
      <div className="yg-topbar-orb" aria-hidden="true" />
      <div>
        <span className="yg-topbar-name">Yahoogle</span>
        <span className="yg-topbar-sub hidden sm:block">Email compliance</span>
      </div>
    </div>
  );
}

function Sidebar() {
  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    ["yg-nav-item", isActive ? "active" : ""].filter(Boolean).join(" ");

  return (
    <ul className="space-y-1" role="list">
      <li>
        <NavLink
          to="/"
          end
          className={navItemClass}
          aria-label="Dashboard"
        >
          <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </NavLink>
      </li>
      <li>
        <NavLink
          to="/domains"
          className={navItemClass}
          aria-label="Domains"
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          Domains
        </NavLink>
      </li>
    </ul>
  );
}
